import React, { useContext, useState } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router";
import { Logout } from "@mui/icons-material";
import { getRoleColor, getRoleLabel } from "../utils/UserRoles";
import { PageContext } from "../contexts/PageContext";
import { useMsal } from "@azure/msal-react";
import Logo from "../assets/TechMageLogoWhite.svg";
import { useSidebarContext } from "../contexts/SideBarContext";

interface NavItem {
  text: string;
  icon: React.ReactNode;
  href?: string;
}

interface SidebarProps {
  drawerWidthCollapsed: number;
  drawerWidthOpen: number;
  navItems: NavItem[];
}

const getInitials = (fullName: string) =>
  fullName
    .split(" ")
    .map((name) => name[0].toUpperCase())
    .join("")
    .slice(0, 2);

export default function SideBar({
  drawerWidthCollapsed,
  drawerWidthOpen,
  navItems,
}: SidebarProps) {

  const { isCollapsed, toggleDrawer, recentSceneId } = useSidebarContext();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { isLoggedIn, user } = useContext(PageContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const avatarButtonRef = React.useRef<HTMLLIElement>(null);
  const handleLogout = async () => {
    try {
      instance.logoutRedirect({
        postLogoutRedirectUri: "https://techmage.us",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? drawerWidthCollapsed : drawerWidthOpen,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isCollapsed ? drawerWidthCollapsed : drawerWidthOpen,
          boxSizing: "border-box",
          backgroundColor: "#171717",
          color: "#ffffff",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          padding: 0,
          minHeight: 64,
        }}
      >
        <IconButton
          onClick={toggleDrawer}
          sx={{
            color: "#ffffff",
            ...(isCollapsed ? {} : { ml: "13px" }),
          }}
        >
          <MenuIcon />
        </IconButton>
        {!isCollapsed && (
          <Box
            component="img"
            src={Logo}
            alt="Logo"
            sx={{
              height: 20,
              marginTop: 0.4,
              marginLeft: 2,
            }}
          />
        )}
      </Box>
      <Divider sx={{ borderColor: "#333" }} />

      {/* Navigation List */}
      <List sx={{ flexGrow: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
            {isCollapsed ? (
              <Tooltip title={item.text} placement="right">
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: "center",
                    px: 2.5,
                  }}
                  onClick={() =>
                    item.text === "Scenes" && recentSceneId
                      ? navigate(`/sim/${recentSceneId}`)
                      : item.href && navigate(item.href)
                  }
                >
                  <ListItemIcon sx={{ minWidth: 0, color: "#ffffff" }}>
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: "initial",
                  px: 2.5,
                }}
                onClick={() =>
                  item.text === "Scenes" && recentSceneId
                    ? navigate(`/sim/${recentSceneId}`)
                    : item.href && navigate(item.href)
                }
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 3, color: "#ffffff" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>



      <Divider sx={{ borderColor: "#333" }} />
      {/* Avatar Section */}
      {user && isLoggedIn && (
        <>
          <ListItem
            ref={avatarButtonRef} // Attach ref to the ListItem
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isCollapsed ? "center" : "flex-start",
              padding: "16px",
              cursor: "pointer",
            }}
            onClick={handleAvatarClick}
          >
            <ListItemIcon
              sx={{
                justifyContent: "center",
                minWidth: "unset",
              }}
            >
              <Avatar sx={{ bgcolor: getRoleColor(user.role), color: "white" }}>
                {getInitials(user.name)}
              </Avatar>
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#ffffff",
                      fontWeight: 500,
                      marginLeft: "15px",
                      marginTop: 0,
                    }}
                  >
                    {user.name}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#aaaaaa",
                      fontWeight: 400,
                      marginLeft: "15px",
                    }}
                  >
                    {getRoleLabel(user.role)}
                  </Typography>
                }
              />
            )}
          </ListItem>

          {/* Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#2e2e2e",
                color: "#ffffff",
                borderRadius: 2,
                mt: 2,
                width: "200px",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                navigate("/my-account");
              }}
            >
              <Avatar
                sx={{
                  bgcolor: getRoleColor(user.role),
                  color: "white",
                  width: 34,
                  height: 34,
                  mr: 2,
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              My Account
            </MenuItem>
            <Divider />
            {/* <MenuItem onClick={handleClose}>
              <Settings fontSize="small" sx={{ mr: 2 }} />
              Settings
            </MenuItem> */}
            <MenuItem
              onClick={() => {
                handleClose();
                handleLogout();
              }}
            >
              <Logout fontSize="small" sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </>
      )}
    </Drawer>
  );
};


